/**
 * Configuration contracts for the assets module (ADR 0003 / epic ALC-1655).
 *
 * Four tables decide everything the module does, and until this file existed none of them
 * could be reached by anything except the seeder: `AssetCapitalizationRule` was written from
 * one private method and read from one, `AssetTenantPolicy` and `FixedAssetClassBookRule` had
 * no writer at all. A tenant whose rules were never seeded gets an empty review queue and no
 * way to find out why — `AssetCandidateService` returns 0 when no rule exists, silently.
 *
 * ## The line these shapes draw
 *
 * The question that decides whether a field appears in a request type here is not "will the
 * client want to change it" — they want to change everything — but **whose judgement is it,
 * legally**. CPC 27 §51 puts useful life on the entity: it is the client's estimate, reviewed
 * annually, and locking it would mean Alchemy exercising a judgement that is not its own. A
 * fiscal classification key is the opposite: opening it transfers a responsibility to someone
 * with no way to evaluate it.
 *
 * - **Level 1 — locked.** `FixedAssetClass.assetKind` (the tax-resolution key), `.code` (the
 *   stable identifier jurisdiction defaults resolve against), `.group` (the CPC disclosure
 *   group), `.isRightOfUse`, and `Book.frameworkCode`. None of these appear in any request
 *   type below. The API refuses them with `ASSET_CONFIG_FIELD_IMMUTABLE` rather than ignoring
 *   them, so a caller that sends one learns that it did.
 * - **Level 2 — entity policy, writable, attributed.** Useful life and residual per book, the
 *   capitalisation floor, unitisation, the incremental borrowing rate, the low-value cut, the
 *   short-term maximum, free-text names. Every write records who and when.
 * - **Level 3 — per-transaction exception.** Out of scope; nothing here forecloses it.
 *
 * ## Warnings are not errors
 *
 * Several changes here are legitimate and also dangerous — deactivating the last active rule
 * switches the whole review queue off. Those come back as `warnings` on a 2xx, never as a 400:
 * refusing them would make the API the authority on a judgement that belongs to the client.
 */
import type {
  AssetUnitizationPolicy,
  DepreciationMethod,
  FixedAssetGroup,
  PostingMode,
  RowStatus,
} from './../generated/enums.js';

/**
 * A consequence the caller should know about, attached to a write that succeeded.
 *
 * `code` is stable and is what the frontend keys its pt-BR copy on; `message` is a pt-BR
 * fallback so a caller that has not learned the code still says something true.
 */
export interface AssetConfigurationWarning {
  code: AssetConfigurationWarningCode;
  message: string;
  /** Free-form context — the affected count, the rule left standing, and so on. */
  details?: Record<string, string | number | boolean | null>;
}

export const ASSET_CONFIGURATION_WARNING_CODES = [
  /** Deactivating this rule leaves the tenant with no active rule: the review queue stops. */
  'ASSET_CONFIG_LAST_ACTIVE_RULE_DEACTIVATED',
  /** The rule matches on description keyword only — it fires on anything worded similarly. */
  'ASSET_CONFIG_KEYWORD_ONLY_RULE',
  /** `allowAutoConfirm` was turned on: matched lines will capitalise with no human review. */
  'ASSET_CONFIG_AUTO_CONFIRM_ENABLED',
  /** A book rule changed. Assets already in service keep the régua frozen at capitalisation. */
  'ASSET_CONFIG_BOOK_RULE_NOT_RETROACTIVE',
  /** The capitalisation floor moved. Candidates already raised keep their snapshotted floor. */
  'ASSET_CONFIG_THRESHOLD_NOT_RETROACTIVE',
  /**
   * The rule's GL account belongs to a DEPRECATED CoA version. The classifier resolves such an
   * account forward through the version chain's crosswalk, and drops the rule outright when
   * that crosswalk is missing or ambiguous — so the rule may never fire. Warned, not refused:
   * a superseded version that is still an ancestor of the live one resolves fine.
   */
  'ASSET_CONFIG_RULE_ACCOUNT_SUPERSEDED_COA',
  /**
   * Step 1 (2026-09-04). Saving a statutory régua for a tenant-created class (no `assetKind`,
   * so no Anexo III line to protect) auto-created a mirror rule in an active fiscal book that
   * had none for this class yet. The mirror copies method, useful life, residual and reference
   * rate, and is never created over an existing fiscal rule.
   */
  'ASSET_CONFIG_FISCAL_RULE_MIRRORED',
] as const;
export type AssetConfigurationWarningCode = (typeof ASSET_CONFIGURATION_WARNING_CODES)[number];

/**
 * Stable error codes. Every one of these is also a pt-BR string in the web `assetsConfig`
 * namespace — an error code with no translation is a 400 the client cannot act on.
 */
export const ASSET_CONFIGURATION_ERROR_CODES = [
  /** A level-1 field was present in the request body. Named in `message`. */
  'ASSET_CONFIG_FIELD_IMMUTABLE',
  /** No identifying matcher: the rule would claim every line on every bill. */
  'ASSET_CONFIG_RULE_NO_MATCHER',
  /** Only `matchMinUnitAmount` was set. An amount qualifies a matcher; it is not one. */
  'ASSET_CONFIG_RULE_AMOUNT_ONLY_MATCHER',
  /** Keyword-only rule without `acknowledgeKeywordOnly`. */
  'ASSET_CONFIG_RULE_KEYWORD_ONLY_UNACKNOWLEDGED',
  /** Keyword-only rules may never auto-confirm, acknowledged or not. */
  'ASSET_CONFIG_RULE_KEYWORD_ONLY_AUTO_CONFIRM',
  /** `matchCfopCode` is not exactly four digits. */
  'ASSET_CONFIG_RULE_CFOP_INVALID',
  /** `matchNcmPrefix` is not digits. */
  'ASSET_CONFIG_RULE_NCM_INVALID',
  /** The target class does not exist, is not ACTIVE, or belongs to another tenant. */
  'ASSET_CONFIG_CLASS_NOT_FOUND',
  /** A tenant-local class code must be unique within the tenant. */
  'ASSET_CONFIG_CLASS_CODE_DUPLICATE',
  /** Class codes are stable uppercase identifiers, not display labels. */
  'ASSET_CONFIG_CLASS_CODE_INVALID',
  /** RIGHT_OF_USE is the only disclosure group that may carry isRightOfUse=true. */
  'ASSET_CONFIG_CLASS_GROUP_ROU_MISMATCH',
  /** Tenant-created classes belong to a statutory framework, never the law-backed tax rail. */
  'ASSET_CONFIG_CLASS_FISCAL_FRAMEWORK_LOCKED',
  /** The selected accounting framework is unknown or inactive. */
  'ASSET_CONFIG_FRAMEWORK_NOT_FOUND',
  /** Book codes are unique within a tenant. */
  'ASSET_CONFIG_BOOK_CODE_DUPLICATE',
  /** A fiscal measurement book needs an existing statutory entity scope to mirror. */
  'ASSET_ONBOARDING_STATUTORY_BOOK_REQUIRED',
  /** The selected entity already has an active TAX_BASIS measurement scope. */
  'ASSET_ONBOARDING_FISCAL_BOOK_EXISTS',
  /** Defensive refusal when the database does not derive TAX_BASIS as MEMO. */
  'ASSET_ONBOARDING_FISCAL_BOOK_NOT_MEMO',
  /** The class is a right-of-use class: bill lines never capitalise into one. */
  'ASSET_CONFIG_RULE_ROU_CLASS_NOT_TARGETABLE',
  /**
   * The rule's GL account lives in a DRAFT CoA version — one no bill was ever classified
   * against. `AssetCandidateService.loadActiveRules` drops such a rule rather than matching it,
   * so it would sit in the list looking configured and decide nothing.
   */
  'ASSET_CONFIG_RULE_ACCOUNT_NOT_MATCHABLE',
  'ASSET_CONFIG_RULE_NOT_FOUND',
  'ASSET_CONFIG_ENTITY_NOT_FOUND',
  'ASSET_CONFIG_BOOK_NOT_FOUND',
  'ASSET_CONFIG_BOOK_RULE_NOT_FOUND',
  /**
   * The book measures under `TAX_BASIS` and the class is curated (`assetKind !== null`): its
   * life comes from IN RFB 1.700/2017 Anexo III, not the tenant's estimate. Step 1
   * (2026-09-04) narrowed this to curated classes only — a tenant-created class's own fiscal
   * régua is writable, since Anexo III has no line for it.
   */
  'ASSET_CONFIG_BOOK_RULE_FISCAL_LOCKED',
  /** Only STRAIGHT_LINE and NONE run in the schedule engine today (step 1, 2026-09-04). */
  'ASSET_CONFIG_METHOD_NOT_IMPLEMENTED',
  'ASSET_CONFIG_POLICY_NOT_FOUND',
  /** Useful life must be a positive whole number of months. */
  'ASSET_CONFIG_USEFUL_LIFE_INVALID',
  /** Residual is a decimal fraction of cost, in [0, 1). A residual of 1 depreciates nothing. */
  'ASSET_CONFIG_RESIDUAL_RATE_INVALID',
  /** A rate expressed as a decimal fraction was outside a believable band. */
  'ASSET_CONFIG_RATE_OUT_OF_RANGE',
  /** A monetary policy amount was negative. */
  'ASSET_CONFIG_AMOUNT_INVALID',
  /** Short-term lease exemption bound must be a positive number of months. */
  'ASSET_CONFIG_SHORT_TERM_MONTHS_INVALID',
  /** Deleting is not a transition this surface offers. Use `status`. */
  'ASSET_CONFIG_DELETE_NOT_SUPPORTED',
] as const;
export type AssetConfigurationErrorCode = (typeof ASSET_CONFIGURATION_ERROR_CODES)[number];

/** The status transitions this surface offers. `DELETED` is deliberately not one of them. */
export const ASSET_CONFIG_STATUSES = ['ACTIVE', 'INACTIVE'] as const;
export type AssetConfigStatus = (typeof ASSET_CONFIG_STATUSES)[number];

// ─────────────────────────────────────────────────────────────────────────────
// Capitalisation rules — the table that decides asset vs expense
// ─────────────────────────────────────────────────────────────────────────────

/**
 * A rule as the settings screen renders it.
 *
 * Matchers are returned both raw (so a form can round-trip them) and as `matcherSummary`,
 * a pt-BR phrase naming what the rule actually tests. A screen that shows five nullable
 * columns makes the reader reconstruct the AND; the phrase states it.
 */
export interface AssetCapitalizationRuleView {
  ruleId: string;
  /** NULL = applies to every entity in the tenant. */
  entityId: string | null;
  entityName: string | null;
  classId: string;
  classCode: string;
  className: string;
  name: string;
  /** Lower value evaluated first; first match wins. */
  priority: number;

  matchGlAccountId: string | null;
  matchGlAccountCode: string | null;
  matchGlAccountName: string | null;
  /**
   * Which CoA version the matched account belongs to, and that version's status.
   *
   * Load-bearing, not decoration. An `Account` row belongs to exactly one CoA version
   * (`@@id([tenantId, glAccountId])`), and the classifier only uses this rule directly when
   * that version is the one the bill was classified against — otherwise it walks the version
   * chain and crosswalks the account forward, and **drops the rule entirely** when the
   * crosswalk is missing or ambiguous. A rule keyed to an account in a stale version therefore
   * looks configured and decides nothing, which is the failure mode this surface exists to
   * make visible.
   */
  matchGlAccountCoaVersionId: string | null;
  matchGlAccountCoaVersionStatus: string | null;
  matchVendorId: string | null;
  matchVendorName: string | null;
  matchNcmPrefix: string | null;
  matchCfopCode: string | null;
  matchDescriptionKeyword: string | null;
  /** Decimal fraction of currency, per unit — never compared against the line total. */
  matchMinUnitAmount: number | null;
  /** Human-readable AND of every matcher this rule declares. */
  matcherSummary: string;

  unitizationPolicyOverride: AssetUnitizationPolicy | null;
  /**
   * True means matched lines may capitalise with no human confirmation. The highest-
   * consequence flag on this surface: a wrong capitalisation is an accounting error, not a
   * UX annoyance.
   */
  allowAutoConfirm: boolean;
  status: RowStatus;

  /**
   * Provenance. Non-null means the system wrote this rule from a human's decision on one
   * ambiguous candidate rather than a curator authoring it. Surfaced so rule quality can be
   * measured separately — a learned rule has one data point behind it.
   */
  learnedFromCandidateId: string | null;
  createdByUserId: string | null;
  createdByUserName: string | null;
  createdAt: string;
  updatedAt: string | null;
}

export interface ListAssetCapitalizationRulesRequest {
  /** Restrict to rules scoped to this entity. Tenant-wide rules are always included. */
  entityId?: string;
  classId?: string;
  status?: RowStatus;
  /** `true` returns only learned rules, `false` only curated ones. */
  learnedOnly?: boolean;
}

export interface ListAssetCapitalizationRulesResponse {
  rules: AssetCapitalizationRuleView[];
  /**
   * How many rules are ACTIVE tenant-wide. Zero means the review queue is switched off for
   * every bill, which is the single most important fact this screen can tell anyone.
   */
  activeRuleCount: number;
}

/**
 * Creating a rule.
 *
 * Every matcher is optional individually and constrained collectively: at least one
 * *identifying* matcher (account, vendor, NCM, CFOP, keyword) must be present.
 * `matchMinUnitAmount` is a qualifier — "and only above R$ 5.000" — and cannot stand alone,
 * because a rule whose only test is an amount claims every line above it regardless of what
 * was bought.
 */
export interface CreateAssetCapitalizationRuleRequest {
  entityId?: string | null;
  classId: string;
  name: string;
  priority?: number;

  matchGlAccountId?: string | null;
  matchVendorId?: string | null;
  matchNcmPrefix?: string | null;
  matchCfopCode?: string | null;
  matchDescriptionKeyword?: string | null;
  matchMinUnitAmount?: number | null;

  unitizationPolicyOverride?: AssetUnitizationPolicy | null;
  /**
   * Must be sent explicitly to be true. Never defaulted on: opting a rule into posting
   * without review is a decision, and a decision has to be made rather than inherited.
   */
  allowAutoConfirm?: boolean;
  /**
   * Required when the only identifying matcher is `matchDescriptionKeyword`. The rule is
   * allowed with it, refused without — see the service docblock for why this is an
   * acknowledgement rather than a refusal.
   */
  acknowledgeKeywordOnly?: boolean;
}

/**
 * Updating a rule. Every field optional; absent means unchanged.
 *
 * Sending `null` for a matcher clears it — which is how a rule is narrowed or widened — so
 * the whole-rule matcher validation re-runs against the merged result, not the patch.
 */
export interface UpdateAssetCapitalizationRuleRequest {
  classId?: string;
  name?: string;
  priority?: number;
  entityId?: string | null;

  matchGlAccountId?: string | null;
  matchVendorId?: string | null;
  matchNcmPrefix?: string | null;
  matchCfopCode?: string | null;
  matchDescriptionKeyword?: string | null;
  matchMinUnitAmount?: number | null;

  unitizationPolicyOverride?: AssetUnitizationPolicy | null;
  allowAutoConfirm?: boolean;
  acknowledgeKeywordOnly?: boolean;
}

export interface SetAssetCapitalizationRuleStatusRequest {
  status: AssetConfigStatus;
}

export interface AssetCapitalizationRuleMutationResult {
  rule: AssetCapitalizationRuleView;
  warnings: AssetConfigurationWarning[];
}

// ─────────────────────────────────────────────────────────────────────────────
// Tenant policy — the thresholds and the lease judgements
// ─────────────────────────────────────────────────────────────────────────────

export interface AssetTenantPolicyView {
  policyId: string;
  /** NULL = the tenant-wide default. A row with an entityId overrides it for that entity. */
  entityId: string | null;
  entityName: string | null;

  /**
   * Applied PER UNIT, never against the line or document total. A class may override it.
   * Aggregating sub-threshold units into a synthetic asset produces something nobody can
   * ever dispose of, so there is no per-tenant opt-out.
   */
  capitalizationThresholdAmount: number;

  /**
   * Decimal fraction (0.137500 = 13,75% a.a.). There is no official rate: this is the
   * company's own incremental borrowing rate and only it knows what that is.
   */
  defaultIncrementalBorrowingRate: number;
  /** CPC 06 fixes no amount for the low-value exemption. This is tenant policy. */
  lowValueLeaseThresholdAmount: number;
  shortTermLeaseMaxMonths: number;
  capLeaseholdImprovementToTerm: boolean;

  updatedAt: string | null;
  updatedByUserId: string | null;
  updatedByUserName: string | null;
  createdAt: string;
}

export interface ListAssetTenantPoliciesResponse {
  policies: AssetTenantPolicyView[];
  /** True when no tenant-wide default exists — the module is unseeded and decides nothing. */
  missingTenantDefault: boolean;
}

/**
 * Save the policy for one scope. `entityId: null` addresses the tenant-wide default.
 *
 * Every field is required rather than patchable: a policy row read as a whole and written as
 * a whole cannot be half-changed by two concurrent editors, and there are only six fields.
 */
export interface SaveAssetTenantPolicyRequest {
  entityId?: string | null;
  capitalizationThresholdAmount: number;
  defaultIncrementalBorrowingRate: number;
  lowValueLeaseThresholdAmount: number;
  shortTermLeaseMaxMonths: number;
  capLeaseholdImprovementToTerm: boolean;
}

export interface AssetTenantPolicyMutationResult {
  policy: AssetTenantPolicyView;
  warnings: AssetConfigurationWarning[];
}

// ─────────────────────────────────────────────────────────────────────────────
// Per-class, per-book rules — the CPC 27 §51 judgement
// ─────────────────────────────────────────────────────────────────────────────

export interface FixedAssetClassBookRuleView {
  classRuleId: string;
  classId: string;
  classCode: string;
  className: string;
  /**
   * Level 1. NULL on a tenant-created class — the same signal `AssetConfigurationClassView`
   * exposes. Carried here too so a book-rule-scoped view (the edit dialog, the onboarding
   * wizard) can tell whether THIS class's fiscal régua is curated (locked,
   * `ASSET_CONFIG_BOOK_RULE_FISCAL_LOCKED`) or the tenant's own estimate (writable) without a
   * second request against the class list.
   */
  classAssetKind: string | null;
  bookId: string;
  bookCode: string;
  bookName: string;
  /** Which framework the book reports under. Locked — level 1. Returned for display only. */
  bookFrameworkCode: string | null;
  /**
   * Whether this book posts to the ledger or only measures alongside it. Derived in the
   * database from `frameworkCode` (ADR 0003 Decision 1), so it is returned rather than
   * re-derived by every caller that needs to tell a régua fiscal from a societária.
   */
  bookPostingMode: PostingMode;
  /** ISO 4217, for formatting amounts measured under this book. */
  bookReportingCurrency: string;

  method: DepreciationMethod;
  usefulLifeMonths: number;
  /** Decimal fraction of cost (0.100000 = 10%). */
  residualRate: number;
  /** Informational: `usefulLifeMonths` is what the engine uses. */
  referenceAnnualRate: number | null;
  /** "CPC 27", "IN RFB 1.700/2017 Anexo III". Required on every book rule for audit. */
  normReference: string | null;

  createdAt: string;
  updatedAt: string | null;
}

export interface ListFixedAssetClassBookRulesRequest {
  classId?: string;
  bookId?: string;
}

export interface ListFixedAssetClassBookRulesResponse {
  bookRules: FixedAssetClassBookRuleView[];
}

/**
 * Create or replace the rule for one (class, book) pair.
 *
 * **Not retroactive, by design.** `AssetCapitalizationService.createBookRules` copies these
 * values onto `FixedAssetBookRule` at capitalisation, and the depreciation engine reads only
 * that frozen copy — so editing a class rule changes what the *next* asset is measured by and
 * leaves every asset already in service alone. Changing an in-service asset's useful life is
 * a CPC 23 change in estimate applied prospectively, which is a different act with a different
 * audit trail; it is not what this endpoint does, and the response says so in a warning.
 */
export interface SaveFixedAssetClassBookRuleRequest {
  classId: string;
  bookId: string;
  method: DepreciationMethod;
  usefulLifeMonths: number;
  residualRate: number;
  referenceAnnualRate?: number | null;
  normReference?: string | null;
}

export interface FixedAssetClassBookRuleMutationResult {
  bookRule: FixedAssetClassBookRuleView;
  warnings: AssetConfigurationWarning[];
}

// ─────────────────────────────────────────────────────────────────────────────
// Asset classes — read, and the level-2 half of the update
// ─────────────────────────────────────────────────────────────────────────────

/**
 * A class as the configuration screen sees it, unlike `FixedAssetClassOption` in `registry`,
 * which is the picker's shape for one entity. The locked fields are returned — the reader
 * needs to see `assetKind` to understand which jurisdiction default applies — and are simply
 * not accepted back.
 */
export interface AssetConfigurationClassView {
  classId: string;
  /** Level 1. Jurisdiction defaults resolve against it. */
  code: string;
  name: string;
  description: string | null;
  /** Level 1. The balance-sheet group and therefore the disclosure standard. */
  group: FixedAssetGroup;
  /** Level 1. The tax-nature key. NULL on a class created by hand: no jurisdiction default. */
  assetKind: string | null;
  /**
   * Level 1. Which accounting framework wrote this class. An entity only sees the classes of
   * the frameworks its own books report under, so this is also what the screen filters on.
   */
  frameworkCode: string;

  /** NULL falls back to the tenant policy floor. */
  capitalizationThresholdAmount: number | null;
  unitizationPolicy: AssetUnitizationPolicy;
  isLeaseholdImprovement: boolean;
  /** Level 1. ROU presentation must stay separable from owned PP&E. */
  isRightOfUse: boolean;
  /**
   * Level 2 (ALC-3489, ADR 0017 D8). The class has no foreseeable limit to the period over
   * which it generates cash (CPC 04 §107) — marcas, domínios — so it is not amortised and is
   * tested for impairment once a year instead (CPC 01 §10).
   *
   * True only where the class's statutory book rule uses method `NONE`: the flag is the
   * statement and the rule is what actually stops the régua, and a flagged class that kept
   * amortising would be a disclosure contradicting its own measurement.
   */
  indefiniteUsefulLife: boolean;
  status: RowStatus;

  /** How many book rules this class carries. Zero means nothing capitalises into it. */
  bookRuleCount: number;
  /** How many assets are in service under it. Non-zero forbids hard deletion forever. */
  assetCount: number;

  createdAt: string;
  updatedAt: string | null;
}

export interface ListAssetConfigurationClassesRequest {
  status?: RowStatus;
  group?: FixedAssetGroup;
}

export interface ListAssetConfigurationClassesResponse {
  classes: AssetConfigurationClassView[];
}

/**
 * Create a tenant-owned disclosure class.
 *
 * `assetKind` is intentionally absent: it is the key into jurisdiction defaults, so a tenant
 * class starts without one and therefore receives no invented fiscal useful life. `isRightOfUse`
 * is accepted only as an explicit consistency assertion; the server derives the persisted value
 * from `group`, preventing the two columns from ever disagreeing.
 */
export interface CreateAssetConfigurationClassRequest {
  code: string;
  name: string;
  description?: string | null;
  group: FixedAssetGroup;
  frameworkCode: string;
  capitalizationThresholdAmount?: number | null;
  unitizationPolicy: AssetUnitizationPolicy;
  isLeaseholdImprovement?: boolean;
  isRightOfUse?: boolean;
}

/**
 * The level-2 half of a class. `code`, `group`, `assetKind` and `isRightOfUse` are absent
 * because they are Alchemy's knowledge, not the tenant's — sending one is refused with
 * `ASSET_CONFIG_FIELD_IMMUTABLE` rather than dropped, so the caller learns it did not apply.
 *
 * `isLeaseholdImprovement` is level 2 and not level 1: whether a benfeitoria's amortisation is
 * bounded by the lease term is a fact about the client's own contract, which the client knows
 * and Alchemy does not.
 */
export interface UpdateAssetConfigurationClassRequest {
  name?: string;
  description?: string | null;
  capitalizationThresholdAmount?: number | null;
  unitizationPolicy?: AssetUnitizationPolicy;
  isLeaseholdImprovement?: boolean;
  /**
   * ADR 0017 D8. Level 2 and update-only: a class is created with no book rule at all, and the
   * flag may only be set where the statutory rule already says `NONE`, so there is nothing for
   * `true` to be consistent with at creation time. Setting it against a rule that still
   * amortises is refused with `ASSET_CLASS_INDEFINITE_LIFE_REQUIRES_NONE`, and so is moving
   * that rule off `NONE` while the flag stands.
   */
  indefiniteUsefulLife?: boolean;
  status?: AssetConfigStatus;
}

export interface AssetConfigurationClassMutationResult {
  assetClass: AssetConfigurationClassView;
  warnings: AssetConfigurationWarning[];
}

// ─────────────────────────────────────────────────────────────────────────────
// Assets module onboarding — derived readiness, never a completion flag
// ─────────────────────────────────────────────────────────────────────────────

export interface AssetOnboardingBookView {
  bookId: string;
  code: string;
  name: string;
  frameworkCode: string;
  reportingCurrency: string;
}

export interface AssetOnboardingClassRuleView {
  classId: string;
  classCode: string;
  className: string;
  statutoryRule: FixedAssetClassBookRuleView | null;
  fiscalRule: FixedAssetClassBookRuleView | null;
  /** True only after a person has written the statutory estimate; seeded alignment is false. */
  answeredByEntity: boolean;
}

export interface AssetModuleOnboardingStatusResponse {
  entityId: string;
  statutoryBook: AssetOnboardingBookView | null;
  fiscalBook: AssetOnboardingBookView | null;
  accountMappings: {
    required: number;
    mapped: number;
  };
  classRules: {
    classes: AssetOnboardingClassRuleView[];
    required: number;
    answered: number;
  };
  companyPolicyConfigured: boolean;
}

/** Creates the law-backed fiscal measurement rail for one entity. */
export interface CreateAssetFiscalBookRequest {
  entityId: string;
  code: string;
  name: string;
}

export interface CreateAssetFiscalBookResponse {
  book: AssetOnboardingBookView;
  onboarding: AssetModuleOnboardingStatusResponse;
}
